<template>
<v-card
  outlined
  class="clone-channel fill-height"
  :class="{ 'clone-channel--compact': compact, 'clone-channel--pending': !!card.pending }"
  :style="accent">
  <!-- THE CARD IS THE CONTROL, and it is the only one on itself.

       What stood here was four: the name opened an input in place, the saturation plot opened the
       curve dialog, each of the two figures opened a bare input in its own row, and an unanswered
       card carried a fifth over the pair. Four targets inside a hundred and fifty pixels, each with
       its own scrim, each opening a different kind of edit, and three of them typing into fields the
       width of the value they held. A reader could not tell from looking which parts of a card were
       controls, and an editor of a channel spread across four gestures is not an editor of anything.

       So the card reports and does not edit. One press — anywhere on it — opens the channel's own
       editor, where the name, the two figures that place its curve and the curve itself are one
       screen with room for what each of them means. The affordance is the plot's old one grown to
       the card: the same grey scrim, the same icon, drawn over everything the card holds.

       ON AN UNANSWERED CARD IT IS ALWAYS THERE. `Complete` under a pointer and nothing at rest asks
       the user to go looking for the thing they are being asked for: a card that is not a medium of
       the model yet is not offering an edit, it is asking a question, and a question shown only on
       hover is a question nobody was asked. So it wears the error wash and the word from the moment
       it arrives — light enough to read the two shapes through, deepening under the pointer — and
       the ordinary grey scrim goes back to being what it always was, an affordance that appears
       where the pointer is.

       A real <button> that COVERS the card rather than one that wraps it: a button cannot contain
       the remove button in the head, and the card's own markup is a stack of divs that has no
       business becoming button content. It is empty, so it carries the name of what it opens as its
       aria-label, and it takes the tab stop the four it replaced used to take between them. -->
  <button
    type="button"
    class="clone-channel__edit"
    :title="card.editTitle"
    :aria-label="card.editTitle"
    @click="$emit('edit', card)">
    <span class="clone-overlay clone-overlay--card" :class="{ 'clone-overlay--pending': cover === 'stated' }">
      <!-- The icon and the word ride in a BADGE, which is what carries their contrast. Over the grey
           scrim they are white on a dark ground and the badge is nothing at all; over the pending
           wash they are dark red on a solid white chip, because what is behind them there is the
           card itself — two sparklines, a rule, whatever the channel happens to draw — and text laid
           straight onto that is legible against some of it and not the rest.

           The icon takes no colour prop while the card is pending: Vuetify's `error--text` is
           `!important` and would pin it to the theme's own red, which is the light one the badge
           exists to get away from. -->
      <span class="clone-overlay__badge">
        <v-icon :small="!compact" :x-small="compact" :color="cover === 'stated' ? null : 'white'">
          {{ cover === 'stated' ? 'mdi-pencil-plus-outline' : 'mdi-tune-variant' }}
        </v-icon>
        <span class="clone-overlay__text">{{ cover === 'stated' ? card.pendingLabel : labels.edit }}</span>
      </span>
    </span>
  </button>
  <div class="clone-channel__head">
    <div class="clone-channel__head-row">
      <div class="clone-channel__title">
        <!-- Read, and nowhere near a field. Renaming a channel is one of the things the editor
             does, and a card that offered it in place was offering the fourth gesture of four. -->
        <div class="clone-channel__name text-truncate" :title="card.medium">{{ card.medium }}</div>
        <div v-if="card.originLabel" class="clone-channel__origin text-truncate" :title="card.originTitle">
          {{ card.originLabel }}
        </div>
      </div>
      <!-- Reversible wherever this card is used, so there is nothing between the click and the
           removal. The parent decides whether it may be taken: a form with no medium is refused by
           the validator, and both wizards hide everything below the cards at zero. -->
      <v-btn
        icon
        x-small
        class="clone-channel__remove"
        :disabled="!removable"
        :title="removable ? card.removeTitle : card.removeLastTitle"
        @click="$emit('remove', card)">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
    <!-- What closes the head is a rule, and which rule depends on whether there is anything to say
         with it. Where the channels of one plan spend differently it is a share bar and the rule
         ranks them at a glance. Where they all spend the same it would be the identical bar on every
         card — decoration that reads as a figure — so `sharePercent` comes through null and the head
         closes on the plain hairline the metric cards use between a figure and its footer. -->
    <div v-if="card.sharePercent !== null" class="clone-channel__share" :title="card.shareLabel">
      <div class="clone-channel__share-fill" :style="{ width: card.sharePercent + '%' }"></div>
    </div>
    <div v-else class="clone-channel__rule"></div>
  </div>

  <div class="clone-channel__charts">
    <div class="clone-plot">
      <!-- NO BADGE. It said that this curve is no longer the model's own, which is true and is not
           what a card six to a row is read for: the reader is comparing SHAPES across a row, and a
           word in the title line of one of them is the only thing on the card that is about its
           history rather than about the channel. Where the card draws the source curve behind the
           current one the two lines already say it, and better — by how much and in which direction
           — and where it does not, the editor the card opens says it in full. -->
      <div class="clone-plot__title">
        <span class="clone-plot__label text-truncate">{{ labels.saturation }}</span>
      </div>
      <!-- The plot draws and does not open. It was the card's one honest control back when it was
           the only one — the curve is what the dialog was about, so the curve was what was clicked —
           and it is now one report among the card's others, under an overlay that covers all of
           them. -->
      <div v-if="card.saturation" class="clone-plot__figure">
        <svg class="clone-plot__svg" :viewBox="plotViewBox" preserveAspectRatio="none">
          <!-- THE OPTIMAL ZONES, under everything else because they are the ground the curve stands
               on: which ranges of this channel's spend are worth buying, in the model report's own
               five colours and behind the same shape the editor's chart shades them behind. A card
               is scanned six to a row for where a medium stops paying, and a bare sparkline could
               only ever show the shape of the response, never which part of it to be in.

               The parent hands them over already projected — it is the one that knows what this
               box is normalised to — so they are four numbers and a colour each, and the first zone
               is not among them. Optional: a wizard that passes none draws none, and the plot is
               exactly what it was. -->
          <rect
            v-for="band in zones"
            :key="band.key"
            class="clone-plot__zone"
            :x="band.x" y="0" :width="band.width" :height="plotHeight"
            :fill="band.fill"></rect>
          <!-- The wash under the line, where the wizard drawing this card asks for one. The chart
               in CreateModelCompose's editor fills nothing under its curve, and a card that is that
               chart at a sixth of the size must not either — a sixth colour in a box already
               carrying five bands. The clone's cards have no bands and keep it: there, the wash is
               what gives the sparkline a body. -->
          <path v-if="card.saturation.area" class="clone-plot__area" :d="card.saturation.area"></path>
          <!-- The curve the model measured, under the one that replaced it: an edit is read against
               the thing it is an edit OF, which is what the dialog's own chart does with the same two
               lines. Optional and absent for an untouched channel, where it would be a dashed line
               exactly under a solid one. The parent normalises the pair on ONE scale, so the two are
               comparable and neither leaves the box. -->
          <path
            v-if="card.sourceLine"
            class="clone-plot__source"
            :d="card.sourceLine"
            vector-effect="non-scaling-stroke"></path>
          <path class="clone-plot__line" :d="card.saturation.line" vector-effect="non-scaling-stroke"></path>
          <line
            v-if="card.spendMarker !== null"
            class="clone-plot__marker"
            :x1="card.spendMarker" :x2="card.spendMarker" y1="0" :y2="plotHeight"
            vector-effect="non-scaling-stroke"></line>
        </svg>
        <!-- WHERE THIS CHANNEL SATURATES: the place among the five bands where the money stops
             paying, which is the one figure the editor behind this card exists to ask for. Drawn
             exactly as the editor's chart draws it — the same purple, on the same boundary the fifth
             zone begins at — so the card and the chart it opens mark one point rather than each
             marking its own.

             OUTSIDE THE SVG on purpose. The plot is stretched to the card's width through
             preserveAspectRatio=none, and a circle inside it arrives as an ellipse; the parent hands
             this over as two percentages of the box instead, which is the one projection that
             survives the stretch. Optional, and a wizard that passes none draws none.

             `unknown` is the same point before anybody has stated it: a hollow ring on a dashed
             outline with a question inside, which is what the editor draws while its axes are blank.
             The place is real and the magnitude is not, and that is a distinction the card can make
             as cheaply as the chart can. -->
        <span
          v-if="card.saturationPoint"
          class="clone-plot__point"
          :class="{ 'clone-plot__point--unknown': card.saturationPoint.unknown }"
          :style="{ left: card.saturationPoint.left + '%', top: card.saturationPoint.top + '%' }"
          aria-hidden="true">{{ card.saturationPoint.unknown ? '?' : '' }}</span>
      </div>
      <div v-else class="clone-plot__empty">{{ labels.noCurve }}</div>
      <!-- The axis, named at both ends where both ends are worth naming: what the x is, and what the
           rule standing on it marks. Both are OPTIONAL and each absence is a statement — a wizard
           that names neither has a plot standing on no units at all, and one that marks no spend has
           nothing for the second word to name. With one child left, space-between puts it at the
           start, which is where the axis begins. -->
      <div class="clone-plot__axis">
        <span v-if="labels.spend">{{ labels.spend }}</span>
        <span v-if="card.spendMarker !== null">{{ labels.largestMonth }}</span>
      </div>
      <!-- THE POINT THE PLOT MARKS, IN FIGURES. The dot above stands where this channel saturates
           and says nothing about how much money that is, which is the one thing six cards in a row
           are read for: a reader comparing media has to know that this one stops paying at 12k and
           that one at 300k, and until now the only way to that pair was to open the card.

           Under the axis rather than over it, so the plot keeps its own two lines — the drawing and
           the name of what it is drawn against — and the reading follows both. The dot repeats the
           mark on the curve at the size of a full stop, which is what says these two numbers are
           that point rather than a second fact about the channel; the arrow is what says which of
           them is the spend and which is what it returns, exactly as the editor's own pair reads.

           Optional. A wizard that states no such pair — the clone builds every medium out of a model
           that already carries one — passes none and draws none. -->
      <div v-if="card.saturationPair" class="clone-plot__pair" :title="card.saturationPair.title">
        <!-- Hollow and dashed while either figure is missing, which is the same ring the plot draws
             above it and the same statement the editor's dashed frame makes: there is a point here
             and nobody has said where it is. -->
        <span
          class="clone-plot__pair-dot"
          :class="{ 'clone-plot__pair-dot--unknown': card.saturationPair.unknown }"
          aria-hidden="true"></span>
        <span class="clone-plot__pair-value">{{ card.saturationPair.knee }}</span>
        <span class="clone-plot__pair-arrow" aria-hidden="true">&#8594;</span>
        <span class="clone-plot__pair-value">{{ card.saturationPair.ceiling }}</span>
      </div>
    </div>

    <div class="clone-plot">
      <div class="clone-plot__title">
        <span class="clone-plot__label text-truncate">{{ labels.decay }}</span>
      </div>
      <svg v-if="card.decayBars.length > 0" class="clone-plot__svg" :viewBox="plotViewBox" preserveAspectRatio="none">
        <rect
          v-for="(bar, index) in card.decayBars"
          :key="index"
          class="clone-plot__bar"
          :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height"></rect>
      </svg>
      <div v-else class="clone-plot__empty">{{ labels.noCurve }}</div>
      <div class="clone-plot__axis">
        <span>{{ labels.lag }}</span>
      </div>
    </div>
  </div>

  <!-- A card with nothing to list still closes the same way: the block keeps its margin-top:auto so
       cards in a row go on aligning their bottoms, and keeps the bottom padding so the axis labels
       above it are not flush against the card's edge. What it drops is the padding that separated
       the first row from the plots, there being no first row. -->
  <div class="clone-channel__figures" :class="{ 'clone-channel__figures--empty': card.figures.length === 0 }">
    <!-- The one state the editor cannot answer, said in place of the two rows it would be answered
         in: `composeAnchor` found no knee in the imported curve, so no figure typed anywhere can
         place it and the way out is the card's own close. Opaque, because what it replaces is two
         rows that will never hold anything.

         A channel merely UNANSWERED has no band. It says so in three places already — the error
         border, its two rows carrying the word instead of a figure, and the card's own overlay
         wearing that word the moment it is reached — and a fourth would be the only element on this
         page that says a thing already said by the thing it is drawn on top of. -->
    <div
      v-if="cover === 'blocked'"
      class="clone-channel__pending clone-channel__pending--blocked"
      :title="card.pendingTitle">
      <v-icon x-small color="error">mdi-alert-outline</v-icon>
      <span class="clone-channel__pending-text">{{ card.pendingLabel }}</span>
    </div>
    <!-- Every figure is a READING, whoever passes it: something this wizard worked out about the
         channel, printed beside its name. There is no other kind left. There used to be — a figure
         the user had stated, which a card drew in the error colour when it was still unanswered,
         because the card was where it was typed — and the wizard that had them moved both into an
         editor with the fields, the sentences and the chart they belong to. A figure that is asked
         for is drawn where it is answered; a figure that is reported is drawn here. -->
    <div v-for="figure in card.figures" :key="figureKey(figure)" class="clone-figure">
      <span class="clone-figure__label">{{ figure.label }}</span>
      <span class="clone-figure__value">{{ figure.value }}</span>
    </div>
  </div>
</v-card>
</template>

<script>
// One channel, as both EKRM wizards draw it: a name, its share of the plan, two inline sparklines
// and a short list of figures.
//
// IT REPORTS AND DOES NOT EDIT. One press anywhere on it emits `edit`, and the wizard opens the
// channel's own editor; the only other control on it is the way out. What it used to carry — a name
// that opened an input in place, a plot that opened the curve dialog, two figures that each opened a
// bare field in their own row, and a cover over the pair of them where they were unanswered — was
// four targets and four scrims inside a hundred and fifty pixels, three of them typing into fields
// the width of the value they held. Nothing about a channel is edited here any more, which is what
// lets every part of the card be drawn for reading.
//
// It owns NO arithmetic. Every path, every percentage, every already-interpolated title arrives on
// the `card` object the parent builds, for the reason SaturationLevers owns none either: no component in this project can be mounted in a test, so anything a test has to
// pin has to live in a util instead. With the drafts gone it now owns no state at all.
//
// The sparklines are hand-rolled svg rather than a chart component because a page carries a dozen
// of these at once and a dozen ECharts instances would be the alternative. That is a decision about
// the RENDERER and not about the picture: the saturation plot is drawn from the same window, the
// same polyline, the same bands and the same point as the chart the card opens — see
// CreateModelCompose's channelCurvePlot, which is where those four are decided once — so what the two
// differ in is the ruler down the side, which a box a hundred pixels wide has no room for anyway.

export default {
  name: 'EkrmChannelCard',
  props: {
    // { medium, originLabel, originTitle, removeTitle, removeLastTitle, editTitle, shareLabel,
    //   sharePercent: number | null, saturation: { area, line } | null, sourceLine: string | null,
    //   spendMarker, decayBars, figures: [{ label, value }] }
    //
    // `zones` is optional: the five optimal ranges of the saturation curve as bands on this card's
    // own plot, [{ key, x, width, fill }], already projected into the plot box by the parent — it is
    // the one that knows what the box was normalised to. The first zone is not among them, being the
    // range the report leaves unpainted. A wizard that passes none draws none.
    //
    // Every figure is a reading, and the list may be empty: a wizard whose cards report nothing but
    // the shape passes none, and the block those rows sat in keeps its place so the cards in a row
    // go on aligning their bottoms.
    //
    // And, optionally, `pending`: 'stated' where the channel is on the page but is not a medium of
    // the model yet and its two figures are what would make it one, or 'blocked' where nothing typed
    // anywhere can place it. `pendingLabel` and `pendingTitle` come with it. A wizard that passes
    // none of the three has no unanswered cards to draw and is untouched — the clone builds every
    // medium out of the model it copies.
    //
    // `sourceLine` is optional and is the curve the source model measured, on the SAME scale as
    // `saturation` — the parent's job, both being normalised together. A wizard that does not pass it
    // draws one line, and what an edit did to it is read in the editor the card opens.
    //
    // `saturation.area` is optional too, and its absence is a statement: a plot whose bands already
    // colour the box wants no wash under its line. See the template.
    //
    // And `saturationPoint` is optional: { left, top, unknown }, the place on the curve where the
    // channel saturates, as percentages of the plot box rather than as svg coordinates — the svg is
    // stretched and a circle drawn inside it would arrive as an ellipse. `unknown` draws it as the
    // question it still is, for a channel whose two figures nobody has typed.
    //
    // `saturationPair` is that same point in figures — { knee, ceiling, unknown, title }, both
    // already formatted by the parent, which is the one that knows what currency they are in and how
    // far an edit has moved them. It is drawn under the saturation plot's axis, so the mark on the
    // curve and the two numbers it stands for are read together. Optional, and `unknown` is what the
    // dot beside them wears while either figure is still unstated.
    card: { type: Object, required: true },
    // Whether this one may be taken out. False on the last card, where removing it would empty the
    // form the restore control lives in.
    removable: { type: Boolean, default: true },
    // The colour this channel is drawn in everywhere else on the page. A left border, because with
    // channels from several models the colour is the only thing tying a card to its stack segment.
    color: { type: String, default: '' },
    plotViewBox: { type: String, required: true },
    plotHeight: { type: Number, required: true },
    // Half the width, and everything in the card sized for it. A wizard that carries three or four
    // channels gives each one a third of a row; one that assembles a dozen from several models needs
    // six across, and at that width the paddings, the type and the 46px plots of the full card stop
    // fitting rather than merely looking large. Nothing here is a different card: the same markup,
    // the same plots and the same arithmetic, only measured for a narrower column. The svg is
    // stretched through preserveAspectRatio=none, so a shorter plot is a css height and neither
    // plotHeight nor plotViewBox moves.
    compact: { type: Boolean, default: false },
    // { saturation, decay, lag, noCurve, edit, spend, largestMonth }. `edit` is the word
    // the card's own overlay wears, so it names what the editor OPENS on and not one section of it.
    // `spend` may be empty, and then the saturation plot's axis carries only the name of the rule on
    // it — see the template.
    labels: { type: Object, required: true }
  },
  computed: {
    accent () {
      return this.color ? { borderLeft: '3px solid ' + this.color } : {}
    },
    // The shaded ranges under the saturation plot, or none. Optional on the card object because one
    // of the two wizards draws curves whose zones it has no business solving — see the prop note.
    zones () {
      return this.card.zones || []
    },
    // Whether this card is standing on a question, and which one. It lasts exactly as long as the
    // question does: until the channel is a medium of the model, which is until BOTH figures are in.
    //
    // It used to lift at the first of the two, when the cover was opaque and answering one figure
    // left something underneath worth reading. The cover is a wash now — it tints the card rather
    // than replacing it — so there is nothing to uncover, and a card half answered is a card still
    // being asked. 'blocked' is the one no figure can answer at all.
    cover () {
      return this.card.pending || ''
    }
  },
  methods: {
    // A figure with no key of its own is keyed by its label, which is what the list was keyed by
    // before any of them could be stated.
    figureKey (figure) {
      return figure.key || figure.label
    }
  }
}
</script>

<style scoped>
  /* Positioned, because the one control on the card is drawn over the whole of it. */
  .clone-channel {
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .clone-channel__head {
    padding: 12px 14px 8px;
  }
  /* The name and the way out of the card on one line, the share bar running the full width under
     both of them. */
  .clone-channel__head-row {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }
  .clone-channel__title {
    flex: 1 1 auto;
    min-width: 0;
  }
  /* Quiet at rest and full strength under the pointer or the focus ring: it is a way out of the
     card, not a figure on it. Always drawn rather than revealed on hover — nothing else here hides
     a control behind one, and a hover-only affordance does not exist on a touch screen. */
  .clone-channel__remove {
    flex: none;
    margin: -2px -4px 0 0;
  }
  /* The icon and not the button: Vuetify gives .v-icon a colour of its own, which a colour on the
     button around it does not reach. */
  /* Above the card's own overlay, and the only thing that is. Removing a channel is not editing it,
     and a way out that a pointer resting on the card covered over would be a way out that could only
     be reached by not looking at the card. */
  .clone-channel__remove {
    position: relative;
    z-index: 3;
  }
  .clone-channel__remove .v-icon {
    color: rgba(0, 0, 0, 0.28);
  }
  .clone-channel__remove:hover .v-icon,
  .clone-channel__remove:focus .v-icon {
    color: rgba(0, 0, 0, 0.7);
  }
  .clone-channel__name {
    display: block;
    font-size: 0.95rem;
    font-weight: 500;
  }
  .clone-channel__origin {
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.45);
  }
  /* The channel's share of the model's monthly media spend, as a rule under its name — enough to
     rank the cards at a glance without a number competing with the four below. */
  .clone-channel__share {
    margin-top: 8px;
    height: 3px;
    border-radius: 2px;
    background-color: rgba(0, 0, 0, 0.07);
    overflow: hidden;
  }
  .clone-channel__share-fill {
    height: 100%;
    background-color: #4B67B8;
  }
  /* The same hairline the metric cards draw between a figure and its footer, at the same weight and
     the same colour, so a card that has no share to show closes its head the way every other card on
     the page closes its own. It stands where the share bar stood, height and all, so swapping one
     for the other moves nothing below it. */
  .clone-channel__rule {
    margin-top: 8px;
    height: 3px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .clone-channel__charts {
    display: flex;
    gap: 10px;
    padding: 4px 14px 0;
  }
  .clone-plot {
    flex: 1 1 0;
    min-width: 0;
  }
  .clone-plot__title {
    display: flex;
    align-items: center;
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 2px;
    /* The row is a label and, on some cards, a badge beside it. Fixing the height keeps the two plots
       of one card level with each other and every card level with its neighbours, so a plot does not
       get shorter on the card that carries an edited curve — a chart resized by a badge. */
    min-height: 20px;
  }
  /* Truncation in a flex row needs this: white-space:nowrap makes the span's min-content its whole
     text, so without it the title pushes the row wider instead of ellipsing. It never bites at full
     width and is what keeps the compact card's row on one line. */
  .clone-plot__label {
    min-width: 0;
  }
  /* THE CARD'S ONE CONTROL: a button lying over the whole of it, holding the scrim and giving it
     something with :hover and :focus-visible to hang off. It is above everything the card draws and
     below the way out — see .clone-channel__remove — so a pointer crossing the card meets the editor
     everywhere except on the one control that is not the editor.

     The radius is the card's own, the scrim reaching the corners of a rounded box and needing to
     stop where they do. */
  .clone-channel__edit {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
  }
  /* Absent until it is wanted, and then over the whole of what it is offered on rather than in a
     corner of it: what is being offered is this CHANNEL, so the target is this channel's card. It
     fades rather than appears, a scrim that snaps on under a pointer crossing a row of six cards
     being a flicker. */
  .clone-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: 2px;
    background: rgba(33, 33, 33, 0.55);
    color: #fff;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    text-transform: none;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  /* The scrim over a whole card is a bigger thing to put on the screen than one over a plot, so it
     is lighter than the one over a plot was: the chip below carries the message, and the card being
     offered is worth going on seeing while it is offered. */
  .clone-overlay--card {
    background: rgba(33, 33, 33, 0.3);
    border-radius: 4px;
  }
  /* WHAT THE ICON AND THE WORD STAND ON, and the whole of their contrast. Laid straight onto the
     scrim they were white over a 30–42% wash of black over a white card — an effective #A0A0A0, so
     about 2.3:1, which is not a legible label at 11px whatever the card happens to draw underneath
     it. On a solid chip the ground is known: white on #212121 at 0.9 is about 10:1, and the pending
     variant below is dark red on white at 5.9. */
  .clone-overlay__badge {
    display: flex;
    align-items: center;
    gap: 4px;
    max-width: 92%;
    min-width: 0;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(33, 33, 33, 0.9);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  /* The same scrim in the error colour on a card that is standing on a question — and unlike the
     grey one, it is NOT waiting for a pointer. It is the card's resting state until the channel has
     both its figures: a question shown only on hover is a question nobody was asked.

     Which is why it is a wash and not a scrim. The grey one is drawn under a pointer, over a card
     the user is already looking at, and can afford to hide it; this one is on the screen the whole
     time, so it tints rather than covers and the two shapes underneath go on being read. It deepens
     under the pointer, which is the only feedback it owes — the chip and the word in it stay exactly
     where they are.

     The tint is a layer rather than a background-color so the chip above it keeps its own solid
     ground: a tinted background on this element would sit under the chip's white and change it. */
  .clone-overlay--pending {
    opacity: 1;
    background: none;
  }
  .clone-overlay--pending::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 4px;
    background: #C62828;
    opacity: 0.1;
    transition: opacity 0.15s ease;
  }
  .clone-channel__edit:hover .clone-overlay--pending::before,
  .clone-channel__edit:focus-visible .clone-overlay--pending::before {
    opacity: 0.18;
  }
  /* The chip, above the tint — which is a layer of its own and would otherwise be drawn over it. A
     solid white ground and a dark red on it: 5.9:1, where the theme's own error on white is 3.3 and
     white on that error is 3.2, both under what 12px type needs. The tenant's colour is not in play
     here at all — only `primary` is per-hostname, `error` is Vuetify's own — so this is a constant
     and not a custom property with a fallback pretending otherwise. */
  .clone-overlay--pending .clone-overlay__badge {
    position: relative;
    background: #ffffff;
    border: 1px solid rgba(198, 40, 40, 0.4);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    color: #C62828;
  }
  .clone-overlay--pending .v-icon {
    color: #C62828;
  }
  .clone-overlay--pending .clone-overlay__text {
    color: #C62828;
    font-weight: 700;
  }
  /* Two rules and not one list: an invalid selector invalidates the whole list it is in, so a
     browser without :focus-visible would lose the hover with it. */
  .clone-channel__edit:hover .clone-overlay {
    opacity: 1;
  }
  .clone-channel__edit:focus-visible .clone-overlay {
    opacity: 1;
  }
  .clone-plot__svg {
    display: block;
    width: 100%;
    height: 46px;
  }
  /* The box the point is placed in. The svg is stretched to the card's width, so nothing inside it
     keeps a shape; the marker is an element over it instead, and this is what its two percentages
     are percentages OF. */
  .clone-plot__figure {
    position: relative;
  }
  /* The saturation point, in the one purple no zone and no line on this card wears — the same
     #7B1FA2 the editor's chart marks it in, and the same one the card beside that chart shows in its
     own dot. Six pixels across, which is the chart's nine on a plot a sixth of the height: a mark
     read against five pale bands rather than a datapoint to be clicked.

     The white ring is what keeps it legible where it lands on the boundary it IS — a colour change
     is exactly the worst background to draw a small dark dot on. */
  .clone-plot__point {
    position: absolute;
    width: 6px;
    height: 6px;
    margin: 0;
    border-radius: 50%;
    background: #7B1FA2;
    box-shadow: 0 0 0 1px #ffffff;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  /* THE SAME PLACE, BEFORE ANYBODY HAS SAID WHERE IT IS. A hollow ring on a dashed outline with a
     question inside it: the shape is true, the boundary among the bands is true, and the magnitude
     is a placeholder — which is precisely the state the editor draws with its axes blank. Bigger
     than the dot because it has a character to hold, and in the card's own ink rather than in the
     purple, that purple meaning a figure somebody stated. */
  .clone-plot__point--unknown {
    width: 13px;
    height: 13px;
    background: #ffffff;
    border: 1px dashed rgba(0, 0, 0, 0.45);
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.55rem;
    font-weight: 700;
    line-height: 1;
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-plot__empty {
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.3);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  /* The zone bands take their colour from the attribute — they are the report's five and the parent
     hands them over — so the rule is about the EDGES. The svg is stretched to the card's width
     through preserveAspectRatio=none, and at that scale two rects meeting on a shared edge are
     anti-aliased into a pale hairline apiece: crispEdges snaps both to the same pixel, and the five
     ranges read as one axis divided rather than as five stripes laid down separately. */
  .clone-plot__zone {
    shape-rendering: crispEdges;
  }
  .clone-plot__area {
    fill: rgba(75, 103, 184, 0.14);
  }
  /* The chart's own first colour, and it has to be exactly that. Both EKRM wizards draw this line
     under a dialog that draws the same curve in ECharts' default palette, and a card in one blue
     opening onto a chart in another is a reader asking what the second line is. It was #4B67B8, a
     shade of this card's own, back when the thumbnail was a picture of the same channel rather than
     the same picture of it. */
  .clone-plot__line {
    fill: none;
    stroke: #5470c6;
    stroke-width: 1.5;
    stroke-linejoin: round;
  }
  /* THE CURVE THE MODEL MEASURED, in the grey the dialog's chart draws it in. Dashed because it is
     the state the channel is no longer in and the solid line is the one being sent; grey because it
     is what an edit is READ AGAINST rather than a second reading of equal weight, which is the same
     reason the chart gave up its own green for #9E9E9E. Thinner than the curve it stands behind, and
     behind it in paint order, so where the two coincide the current one is what is seen. */
  .clone-plot__source {
    fill: none;
    stroke: #9E9E9E;
    stroke-width: 1.25;
    stroke-dasharray: 3 2;
    stroke-linejoin: round;
  }
  .clone-plot__marker {
    stroke: rgba(0, 0, 0, 0.35);
    stroke-width: 1;
    stroke-dasharray: 2 2;
  }
  .clone-plot__bar {
    fill: #7E57C2;
  }
  .clone-plot__axis {
    display: flex;
    justify-content: space-between;
    font-size: 0.62rem;
    color: rgba(0, 0, 0, 0.4);
    padding-top: 2px;
  }

  /* THE TWO FIGURES THE MARK ABOVE STANDS FOR, on one line under the axis they belong to.
     Centred under the plot rather than ranged against its edges: the pair is one statement read left
     to right through the arrow, and space-between would push the two ends apart into two.

     It WRAPS, and that is the whole width rule. Six cards to a row leaves each plot about ninety
     pixels, which two compact amounts and an arrow fit inside for every ordinary figure and not for
     the largest of them; a row that wraps puts the second amount on its own line in that case, where
     a row that could not would either push the decay plot over or cut a number in half. */
  .clone-plot__pair {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: center;
    column-gap: 3px;
    padding-top: 1px;
    font-size: 0.65rem;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.8);
  }
  .clone-plot__pair-value {
    font-weight: 500;
  }
  /* Lighter than the figures it stands between, being the one thing in the row nobody reads twice:
     it says which way the pair goes and then gets out of the way. */
  .clone-plot__pair-arrow {
    color: rgba(0, 0, 0, 0.35);
  }
  /* The mark on the curve, at the size of a full stop, which is what says these two numbers ARE that
     point rather than a second fact about the channel. Same purple as the dot on the plot and as the
     one the editor's own pair carries. Baseline-aligned by hand: a round span has no baseline of its
     own and would otherwise sit on the line the digits stand on. */
  .clone-plot__pair-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #7B1FA2;
    transform: translateY(-1px);
  }
  /* Hollow and dashed while either half is missing — the ring the plot draws above it, at the size
     of the dot it will become. */
  .clone-plot__pair-dot--unknown {
    background: transparent;
    border: 1px dashed rgba(0, 0, 0, 0.45);
  }

  /* The card of a channel that is on the page and is not a medium of the model yet. ONE red for the
     whole state — the border, the chip on the overlay, the unanswered rows and the blocked band —
     and it is deliberately not the theme's: `primary` is per-hostname here and `error` is not, it is
     Vuetify's own #FF5252, which is a light red that reads as 3.3:1 against white at the sizes this
     card uses and as a hairline nobody notices at 1px. The rule out-specifies Vuetify's own outline
     by naming the card's class twice, `.theme--light.v-sheet--outlined` being two classes itself.
     The left border is the channel's colour and stays: it is set inline, and it is the one thing
     tying this card to its segment of every chart below. */
  .clone-channel.clone-channel--pending {
    border-color: #C62828;
  }

  /* Positioned, because the pending overlay is drawn over the whole of it. */
  .clone-channel__figures {
    position: relative;
    margin-top: auto;
    padding: 10px 14px 12px;
  }
  /* What the card is waiting for, IN PLACE OF the two rows that answer it rather than over them.
     Opaque: there is no information underneath — the whole content of this section is that it has
     not been answered — and a translucent cover let two empty labels show through the words asking
     for them, which reads as a card that failed to draw.

     The fill is a white base with the theme's error over it at a tenth, as a layer and not as a
     background-color, because the error is a per-tenant custom property and cannot be given an
     alpha. The base is what makes it opaque; the layer is what makes it read as the same state the
     border is in. The bottom corners are the card's own — this block is the last thing in it, and
     square ones would show past the radius. */
  .clone-channel__pending {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 0;
    border: 0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    background-color: #ffffff;
    color: #C62828;
    font: inherit;
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    overflow: hidden;
  }
  .clone-channel__pending::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #C62828;
    opacity: 0.1;
    transition: opacity 0.2s;
    pointer-events: none;
  }
  .clone-channel__pending:hover::before {
    opacity: 0.2;
  }
  .clone-channel__pending:focus-visible {
    outline: 1px solid #C62828;
    outline-offset: -2px;
  }
  /* Nothing typed here can place this curve, so nothing here is pressed and nothing lifts: the same
     drawing without the affordance, and it stays for as long as the channel does. */
  .clone-channel__pending--blocked {
    cursor: default;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }
  .clone-channel__pending--blocked:hover::before {
    opacity: 0.1;
  }
  /* Above the tint, which is a layer of its own and would otherwise be drawn over the words. */
  .clone-channel__pending .v-icon,
  .clone-channel__pending-text {
    position: relative;
    min-width: 0;
    text-align: center;
    line-height: 1.2;
  }
  .clone-channel__figures--empty {
    padding-top: 0;
  }
  /* THE BAND WITH NOTHING UNDER IT. It is drawn OVER the two rows it stands in place of, and their
     height is the only height it has ever had — so a caller that lists no figures at all leaves it
     covering twelve pixels of padding and nothing legible in them. Where there is nothing to cover
     it stops covering: back into the flow, taking the height of its own words, with the gap above it
     that the first row used to make and all four corners rounded rather than the card's bottom two,
     the block no longer ending flush with the card. */
  .clone-channel__figures--empty .clone-channel__pending {
    position: relative;
    margin-top: 10px;
    padding: 7px 0;
    border-radius: 4px;
  }
  .clone-figure {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    line-height: 1.7;
  }
  .clone-figure__label {
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-figure__value {
    font-weight: 500;
  }

  /* --- Compact ---------------------------------------------------------------------------------
     Six to a row rather than three. Every number below is the full card's own, roughly halved where
     halving it still leaves the thing legible and left alone where it does not: the type stops at
     the sizes this app already uses for a caption rather than scaling all the way down, and the two
     icon buttons keep Vuetify's x-small footprint, being touch targets before they are decoration.
     What actually buys the width back is the padding — 14px either side of a 230px card is a tenth
     of it — and the 10px between two plots that are now about a hundred wide each. */
  .clone-channel--compact .clone-channel__head {
    padding: 8px 9px 6px;
  }
  .clone-channel--compact .clone-channel__name,
  .clone-channel--compact .clone-channel__origin {
    font-size: 0.6rem;
  }
  .clone-channel--compact .clone-channel__share,
  .clone-channel--compact .clone-channel__rule {
    margin-top: 6px;
  }
  .clone-channel--compact .clone-channel__charts {
    gap: 6px;
    padding: 3px 9px 0;
  }
  .clone-channel--compact .clone-plot__title {
    font-size: 0.55rem;
    letter-spacing: 0.03em;
    min-height: 18px;
    margin-bottom: 1px;
  }
  .clone-channel--compact .clone-plot__svg,
  .clone-channel--compact .clone-plot__empty {
    height: 34px;
  }
  .clone-channel--compact .clone-plot__empty {
    font-size: 0.6rem;
  }
  .clone-channel--compact .clone-plot__axis {
    font-size: 0.55rem;
  }
  /* The figures come down with the axis above them and no further: they are the one thing in this
     column a reader compares from card to card, and under 0.6rem two amounts stop being scannable
     at the distance six cards in a row are read from. */
  .clone-channel--compact .clone-plot__pair {
    font-size: 0.6rem;
    column-gap: 2px;
  }
  /* The mark comes down with the plot it stands on. Six pixels on a 34px box is the same mark
     proportionally larger, and the ring has to stay big enough for the character inside it. */
  .clone-channel--compact .clone-plot__point {
    width: 5px;
    height: 5px;
  }
  .clone-channel--compact .clone-plot__point--unknown {
    width: 11px;
    height: 11px;
    font-size: 0.5rem;
  }
  /* The word STAYS on the compact card, where it used to be hidden. It was hidden because the scrim
     was the 46px plot and the word had to fit beside the icon in about a hundred pixels; the scrim is
     now the whole card and stacks the two, so there is room for the one word that says what pressing
     the card does — and on an unanswered card that word is the question. */
  .clone-channel--compact .clone-overlay__text {
    font-size: 0.6rem;
  }
  /* Half the bottom padding the full card keeps. The rule that sets it is the axis labels above:
     they are the last thing in the card and they need clearing, and at 0.55rem there is less of them
     to clear. The top padding is not halved with it — that one separates two things, a plot row and
     a figure row, and it is doing the same job at either size. */
  .clone-channel--compact .clone-channel__figures {
    padding: 8px 9px 4px;
  }
  .clone-channel--compact .clone-figure {
    font-size: 0.68rem;
    line-height: 1.5;
  }
  /* Six to a row leaves about a hundred and fifty pixels, and the word has to survive it: the type
     comes down and the icon keeps its own size, this being the one overlay on the compact card whose
     text is not optional — an icon alone would say something is wrong and not what to do. */
  .clone-channel--compact .clone-channel__pending {
    font-size: 0.6rem;
    gap: 3px;
  }
</style>
